/**
 * Login 2FA Modal
 *
 * Modal component for entering 2FA verification code during login.
 * Displays a 6-digit code input field with countdown timer, attempts remaining,
 * resend functionality, and error handling.
 */

import { useState, useEffect, useCallback } from 'react';
import { Modal } from '../ui/Modal';
import clsx from 'clsx';

export interface Login2FAModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Masked email destination where the code was sent */
  destination: string;
  /** Expiration time in seconds */
  expiresIn: number;
  /** Remaining verification attempts */
  attemptsRemaining: number;
  /** Callback when user submits the code */
  onSubmit: (code: string) => Promise<void>;
  /** Callback when user requests code resend */
  onResendCode: () => Promise<void>;
  /** Whether the verification is in progress */
  isLoading?: boolean;
  /** Error message to display */
  error?: string | null;
}

/**
 * Format remaining seconds as "Xm Xs"
 */
function formatTime(seconds: number): string {
  if (seconds <= 0) return 'Expired';
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  if (minutes > 0) {
    return `${minutes}m ${secs.toString().padStart(2, '0')}s`;
  }
  return `${secs}s`;
}

export const Login2FAModal = ({
  isOpen,
  onClose,
  destination,
  expiresIn,
  attemptsRemaining,
  onSubmit,
  onResendCode,
  isLoading = false,
  error = null,
}: Login2FAModalProps) => {
  const [code, setCode] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [remainingTime, setRemainingTime] = useState(expiresIn);
  const [isResending, setIsResending] = useState(false);
  const [localAttemptsRemaining, setLocalAttemptsRemaining] = useState(attemptsRemaining);

  // Reset state when modal opens or props change
  useEffect(() => {
    if (isOpen) {
      setCode('');
      setLocalError(null);
      setRemainingTime(expiresIn);
      setLocalAttemptsRemaining(attemptsRemaining);
    }
  }, [isOpen, expiresIn, attemptsRemaining]);

  // Countdown timer
  useEffect(() => {
    if (!isOpen || remainingTime <= 0) return;

    const interval = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, remainingTime]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      // Validate code
      if (!code || code.length !== 6) {
        setLocalError('Please enter a 6-digit verification code');
        return;
      }

      if (!/^\d{6}$/.test(code)) {
        setLocalError('Verification code must be 6 digits');
        return;
      }

      if (remainingTime <= 0) {
        setLocalError('Verification code has expired');
        return;
      }

      setLocalError(null);

      try {
        await onSubmit(code);
        // Success - modal will be closed by parent component
      } catch {
        // Error will be passed via error prop from parent
        // Decrement local attempts counter
        setLocalAttemptsRemaining((prev) => Math.max(0, prev - 1));
      }
    },
    [code, remainingTime, onSubmit]
  );

  const handleCodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 6) {
      setCode(value);
      setLocalError(null);
    }
  }, []);

  const handleResendCode = useCallback(async () => {
    setIsResending(true);
    setLocalError(null);

    try {
      await onResendCode();
      setCode('');
    } catch {
      setLocalError('Failed to resend code. Please try again.');
    } finally {
      setIsResending(false);
    }
  }, [onResendCode]);

  const displayError = error || localError;
  const isExpired = remainingTime <= 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" closeOnOverlayClick={!isLoading}>
      <form onSubmit={handleSubmit}>
        <Modal.Header title="Two-Factor Authentication" />
        <Modal.Body>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              A verification code has been sent to{' '}
              <strong className="text-gray-900">{destination}</strong>.
            </p>

            <div className="space-y-2">
              <label htmlFor="verification-code" className="block text-sm font-medium text-gray-700">
                Verification Code
              </label>
              <input
                id="verification-code"
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                value={code}
                onChange={handleCodeChange}
                disabled={isLoading || isExpired}
                placeholder="000000"
                autoComplete="off"
                autoFocus
                className={clsx(
                  'block w-full px-4 py-3 text-center text-2xl font-mono tracking-widest rounded-lg border shadow-sm',
                  'focus:outline-none focus:ring-2',
                  isExpired || displayError
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500',
                  (isLoading || isExpired) && 'opacity-50 cursor-not-allowed'
                )}
              />
              <p className="text-xs text-gray-500 text-center">
                Enter the 6-digit code
              </p>
            </div>

            {/* Timer and attempts info */}
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <span className="text-gray-600">Expires in:</span>
                <span
                  className={clsx(
                    'font-medium',
                    isExpired
                      ? 'text-red-600'
                      : remainingTime <= 30
                      ? 'text-amber-600'
                      : 'text-gray-900'
                  )}
                >
                  {formatTime(remainingTime)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-600">Attempts:</span>
                <span
                  className={clsx(
                    'font-medium',
                    localAttemptsRemaining <= 1 ? 'text-red-600' : 'text-gray-900'
                  )}
                >
                  {localAttemptsRemaining} left
                </span>
              </div>
            </div>

            {/* Error message */}
            {displayError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-600">{displayError}</p>
              </div>
            )}

            {/* Expired warning */}
            {isExpired && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  This verification code has expired. Please click "Resend Code" to receive a new one.
                </p>
              </div>
            )}

            {/* Low attempts warning */}
            {localAttemptsRemaining <= 1 && localAttemptsRemaining > 0 && !isExpired && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  Warning: Only {localAttemptsRemaining}{' '}
                  {localAttemptsRemaining === 1 ? 'attempt' : 'attempts'} remaining before this code is locked.
                </p>
              </div>
            )}

            {/* Resend code link */}
            <div className="text-center">
              <button
                type="button"
                onClick={handleResendCode}
                disabled={isLoading || isResending}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isResending ? 'Sending...' : 'Resend Code'}
              </button>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading || isExpired || code.length !== 6}
            className={clsx(
              'px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500',
              'bg-primary-600 hover:bg-primary-700',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <svg
                  className="animate-spin h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Verifying...
              </span>
            ) : (
              'Verify'
            )}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
